import { isCurrentCultureRightToLeft } from 'owa-localize';
import { useEffect } from 'react';
import { usePointerEvents } from 'owa-react-hooks/lib/usePointerEvents';

/**
 * Hook that enables the whole page to handle drag-and-drop events using pen, touch and mouse.
 *
 * This works by handling pointer events and translating them into drag-and-drop events.
 *
 * Draggable items must have touch-action:none defined in their CSS.
 *
 * Other elements in the page must also be using pointer events, in order to avoid
 * possible conflicts with mouse events.
 *
 * Reference: https://html.spec.whatwg.org/multipage/dnd.html#drag-and-drop-processing-model
 *
 * Notes:
 * - we are dealing with just node sources, not text-selection sources.
 * - we are not raising 'ondrag' on the source element while the operation is ongoing.
 * - we are not affecting the pointer cursor.
 */
export default function useTouchDragDrop() {
    const usingPointerEvents = usePointerEvents();
    useEffect(() => {
        if (usingPointerEvents) {
            document.addEventListener('pointerdown', onPointerDownCapture, true);
            document.addEventListener('pointerdown', onPointerDown);
            document.addEventListener('pointermove', onPointerMove, true);
            document.addEventListener('pointerup', onPointerUpOrCancel, true);
            document.addEventListener('pointercancel', onPointerUpOrCancel, true);
            document.addEventListener('keydown', onKeyDown, true);
        }

        return () => {
            if (usingPointerEvents) {
                reset();
                document.removeEventListener('pointerdown', onPointerDownCapture, true);
                document.removeEventListener('pointerdown', onPointerDown);
                document.removeEventListener('pointermove', onPointerMove, true);
                document.removeEventListener('pointerup', onPointerUpOrCancel, true);
                document.removeEventListener('pointercancel', onPointerUpOrCancel, true);
                document.removeEventListener('keydown', onKeyDown, true);
            }
        };
    }, [usingPointerEvents]);
}

let pointerDownX = 0;
let pointerDownY = 0;
let pointerType = '';
let pointerId = 0;
let sourceNode: HTMLElement | null = null;

let hotSpotX = 0;
let hotSpotY = 0;
let dragDataStore: DataTransfer | null = null;
let customDragPreview: HTMLElement | null = null;
let dragPreview: HTMLElement | null = null;

let lastTarget: Element | null = null;

function reset() {
    dragPreview?.parentElement?.removeChild(dragPreview);

    pointerDownX = 0;
    pointerDownY = 0;
    pointerType = '';
    pointerId = 0;
    sourceNode = null;

    hotSpotX = 0;
    hotSpotY = 0;
    dragDataStore = null;
    customDragPreview = null;
    dragPreview = null;

    lastTarget = null;
}

function onPointerDownCapture(e: PointerEvent) {
    if (sourceNode) {
        e.preventDefault();
        e.stopPropagation();
    }
}

function onPointerDown(e: PointerEvent) {
    const isLeftButtonOnly = e.buttons === 1;
    const detectDragOperation = e.isPrimary && isLeftButtonOnly && !e.defaultPrevented;

    if (detectDragOperation) {
        sourceNode = getDraggableAncestor(e.target as HTMLElement);
        if (sourceNode) {
            pointerDownX = e.clientX;
            pointerDownY = e.clientY;
            pointerType = e.pointerType;
            pointerId = e.pointerId;
            e.stopPropagation();
        }
    }
}

function getDraggableAncestor(e: HTMLElement | null) {
    while (e && !e.draggable) {
        e = e.parentElement;
    }
    return e;
}

function handleAsDragEvent(e: PointerEvent) {
    return e.isPrimary && e.pointerId === pointerId && e.pointerType === pointerType;
}

function onPointerMove(e: PointerEvent) {
    if (!sourceNode || e.defaultPrevented) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!handleAsDragEvent(e)) {
        return;
    }

    const { clientX, clientY } = e;

    let currentTarget = getPointerEventTarget(document.elementFromPoint(clientX, clientY));

    if (!dragPreview) {
        const MIN_DRAG_THRESHOLD_PX = 5;
        const delta = Math.abs(clientX - pointerDownX) + Math.abs(clientY - pointerDownY);
        const hasDraggedFarEnough = delta > MIN_DRAG_THRESHOLD_PX;

        if (hasDraggedFarEnough) {
            const { left, top } = sourceNode.getBoundingClientRect();
            hotSpotX = clientX - left;
            hotSpotY = clientY - top;

            dragDataStore = createDragDataStore();
            if (raise(sourceNode, 'dragstart', e)) {
                reset();
                return;
            }

            const dragPreviewSource = customDragPreview || sourceNode;

            dragPreview = document.createElement('div');
            dragPreview.appendChild(
                copyStyle(dragPreviewSource, dragPreviewSource.cloneNode(true) as HTMLElement)
            );

            const childStyle = (dragPreview.children[0] as HTMLElement).style;
            childStyle.position = 'absolute';
            childStyle.top = childStyle.left = '0';

            const s = dragPreview.style;
            s.position = 'absolute';
            s.top = s.left = '-9999px';
            s.zIndex = '999999';
            s.pointerEvents = 'none';
            s.opacity = '0.85';

            document.body.appendChild(dragPreview);
            customDragPreview?.parentElement?.removeChild(customDragPreview);
        }
    } else {
        const s = dragPreview.style;
        let leftPos = e.pageX - hotSpotX;
        if (isCurrentCultureRightToLeft()) {
            leftPos += document.body.offsetWidth;
        }
        s.left = Math.round(leftPos) + 'px';
        s.top = Math.round(e.pageY - hotSpotY) + 'px';

        if (currentTarget !== lastTarget) {
            raise(lastTarget, 'dragleave', e);
            raise(currentTarget, 'dragenter', e);
            lastTarget = currentTarget;
        }
    }

    raise(currentTarget, 'dragover', e);
}

function getPointerEventTarget(e: Element | null) {
    while (e && getComputedStyle(e).pointerEvents === 'none') {
        e = e.parentElement;
    }
    return e as HTMLElement | null;
}

function onPointerUpOrCancel(e: PointerEvent) {
    if (!sourceNode || e.defaultPrevented) {
        return;
    }

    e.preventDefault();

    if (!handleAsDragEvent(e)) {
        return;
    }

    dragPreview?.parentElement?.removeChild(dragPreview);

    if (dragDataStore) {
        if (e.type === 'pointerup') {
            raise(lastTarget, 'drop', e);
        }

        raise(sourceNode, 'dragend', e);
    }

    reset();
}

function onKeyDown(e: KeyboardEvent) {
    if (sourceNode && e.key == 'Escape') {
        raise(sourceNode, 'dragend', e);
        reset();
    }
}

const COPY_EVENT_PROPERTIES: (keyof PointerEvent)[] = [
    'altKey',
    'ctrlKey',
    'metaKey',
    'shiftKey',
    'pageX',
    'pageY',
    'clientX',
    'clientY',
    'screenX',
    'screenY',
    'button',
    'buttons',
    'which',
];

function raise(
    target: EventTarget | null,
    type: 'dragstart' | 'dragenter' | 'dragover' | 'dragleave' | 'drop' | 'dragend',
    copyFrom: Event | null
) {
    if (target && copyFrom) {
        const evt = document.createEvent('Event');
        evt.initEvent(type, true, true);
        (evt as any).dataTransfer = dragDataStore;

        for (let i = 0; i < COPY_EVENT_PROPERTIES.length; i++) {
            const p = COPY_EVENT_PROPERTIES[i];
            (evt as any)[p] = copyFrom[p];
        }

        target.dispatchEvent(evt);
        return evt.defaultPrevented;
    }

    return false;
}

const REMOVE_STYLE_ATTRIBUTES = ['id', 'class', 'style', 'draggable'];

function copyStyle(src: HTMLElement, dst: HTMLElement) {
    REMOVE_STYLE_ATTRIBUTES.forEach(function (att) {
        dst.removeAttribute(att);
    });

    const cs = getComputedStyle(src);
    for (let i = 0; i < cs.length; i++) {
        const key = cs[i];
        dst.style.setProperty(key, cs.getPropertyValue(key));
    }
    dst.style.pointerEvents = 'none';

    for (let i = 0; i < src.children.length; i++) {
        copyStyle(src.children[i] as HTMLElement, dst.children[i] as HTMLElement);
    }

    return dst;
}

function createDragDataStore(): DataTransfer {
    let data: { [key: string]: string } = {};
    return {
        dropEffect: 'move',
        effectAllowed: 'all',
        get files() {
            return (undefined as any) as FileList;
        },
        get items() {
            return (undefined as any) as DataTransferItemList;
        },
        get types() {
            return Object.keys(data);
        },
        clearData: (format?: string): void => {
            if (format) {
                delete data[format.toLowerCase()];
            } else {
                data = {};
            }
        },
        getData: (format: string): string => {
            return data[format.toLowerCase()] || '';
        },
        setData: (format: string, value: string): void => {
            data[format.toLowerCase()] = value;
        },
        setDragImage: (image: Element, x: number, y: number): void => {
            customDragPreview = image as HTMLElement;
            hotSpotX = x;
            hotSpotY = y;
        },
    };
}
